import React, { useState, useEffect } from 'react';
import { 
  Receipt, Calendar, DollarSign, CreditCard, Banknote, Gift, Phone,
  Download, Filter, RefreshCw, ChevronDown, ChevronUp, TrendingUp,
  Clock, User, Package, ArrowLeft, FileSpreadsheet, Printer
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAYMENT_ICONS = {
  cash: { icon: Banknote, color: 'text-green-600', bg: 'bg-green-100' },
  card: { icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-100' },
  etransfer: { icon: Phone, color: 'text-orange-600', bg: 'bg-orange-100' },
  giftcard: { icon: Gift, color: 'text-purple-600', bg: 'bg-purple-100' },
};

const POSSalesReport = ({ user, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterCashier, setFilterCashier] = useState('');
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'all'

  // Fetch transactions
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      
      if (viewMode === 'daily' && selectedDate) {
        params.append('date', selectedDate);
      }
      if (filterCashier) {
        params.append('cashier_id', filterCashier);
      }

      const res = await fetch(`${API_URL}/api/pos/transactions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions || []);
        setSummary(data.summary || null);
      } else {
        toast.error('Failed to load transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch daily summary
  const fetchDailySummary = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      if (selectedDate) {
        params.append('date', selectedDate);
      }

      const res = await fetch(`${API_URL}/api/pos/daily-summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setDailySummary(data);
      }
    } catch (error) {
      console.error('Error fetching daily summary:', error);
    }
  };

  useEffect(() => {
    fetchTransactions();
    if (viewMode === 'daily') {
      fetchDailySummary();
    }
  }, [selectedDate, filterCashier, viewMode]);

  // Export to CSV
  const exportToCSV = () => {
    if (transactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    const headers = [
      'Order Number',
      'Date/Time',
      'Cashier',
      'Customer',
      'Items',
      'Subtotal',
      'Tax',
      'Discount',
      'Total',
      'Payment Method',
      'Cash Received',
      'Change Due'
    ];

    const rows = transactions.map(t => [
      t.order_number || '',
      new Date(t.created_at).toLocaleString(),
      t.cashier_name || '',
      t.customer_name || 'Walk-in',
      t.items_count || 0,
      t.subtotal?.toFixed(2) || '0.00',
      t.tax?.toFixed(2) || '0.00',
      t.discount?.toFixed(2) || '0.00',
      t.total?.toFixed(2) || '0.00',
      t.payment_method || '',
      t.cash_received?.toFixed(2) || '',
      t.change_due?.toFixed(2) || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pos_sales_report_${selectedDate || 'all'}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // Print report
  const printReport = () => {
    window.print();
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  // Format time
  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-CA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get payment icon component
  const PaymentIcon = ({ method }) => {
    const config = PAYMENT_ICONS[method] || PAYMENT_ICONS.cash;
    const IconComponent = config.icon;
    return (
      <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center`}>
        <IconComponent className={`w-4 h-4 ${config.color}`} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pos-sales-report">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Receipt className="w-6 h-6 text-orange-500" />
                  POS Sales Report
                </h1>
                <p className="text-sm text-gray-500">View and export point-of-sale transactions</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchTransactions} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button variant="outline" onClick={exportToCSV} className="gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                Export CSV
              </Button>
              <Button variant="outline" onClick={printReport} className="gap-2">
                <Printer className="w-4 h-4" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="p-4 mb-6 print:hidden">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm font-medium mb-2 block">View Mode</Label>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'daily' ? 'default' : 'outline'}
                  onClick={() => setViewMode('daily')}
                  className="flex-1"
                >
                  Daily Report
                </Button>
                <Button
                  variant={viewMode === 'all' ? 'default' : 'outline'}
                  onClick={() => setViewMode('all')}
                  className="flex-1"
                >
                  All Transactions
                </Button>
              </div>
            </div>
            
            {viewMode === 'daily' && (
              <div className="w-48">
                <Label className="text-sm font-medium mb-2 block">Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full"
                  data-testid="date-filter"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Daily Summary Cards */}
        {viewMode === 'daily' && dailySummary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Sales */}
            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Sales</p>
                  <p className="text-3xl font-bold text-green-700">{formatCurrency(dailySummary.gross_sales)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                {dailySummary.total_transactions} transactions
              </p>
            </Card>

            {/* Total Tax */}
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Tax</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(dailySummary.total_tax)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2">HST collected</p>
            </Card>

            {/* Cash Drawer */}
            <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Cash Drawer</p>
                  <p className="text-3xl font-bold text-amber-700">{formatCurrency(dailySummary.cash_drawer?.expected_cash)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Received: {formatCurrency(dailySummary.cash_drawer?.cash_received)} | Change: {formatCurrency(dailySummary.cash_drawer?.change_given)}
              </p>
            </Card>

            {/* Discounts */}
            <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Discounts</p>
                  <p className="text-3xl font-bold text-purple-700">{formatCurrency(dailySummary.total_discounts)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-2">Applied discounts</p>
            </Card>
          </div>
        )}

        {/* Payment Method Breakdown */}
        {viewMode === 'daily' && dailySummary && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-gray-500" />
              Payment Method Breakdown
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(dailySummary.payment_breakdown || {}).map(([method, amount]) => {
                const config = PAYMENT_ICONS[method] || PAYMENT_ICONS.cash;
                const IconComponent = config.icon;
                return (
                  <div key={method} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center`}>
                      <IconComponent className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 capitalize">{method.replace('_', ' ')}</p>
                      <p className="text-lg font-bold text-gray-800">{formatCurrency(amount)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Cashier Performance */}
        {viewMode === 'daily' && dailySummary?.by_cashier && Object.keys(dailySummary.by_cashier).length > 0 && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              Cashier Performance
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm text-gray-500">Cashier</th>
                    <th className="text-center py-2 px-3 text-sm text-gray-500">Transactions</th>
                    <th className="text-right py-2 px-3 text-sm text-gray-500">Total Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(dailySummary.by_cashier).map(([cashier, data]) => (
                    <tr key={cashier} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3 px-3 font-medium">{cashier}</td>
                      <td className="py-3 px-3 text-center">
                        <Badge variant="secondary">{data.transactions}</Badge>
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-green-600">
                        {formatCurrency(data.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Transactions List */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-500" />
                Transactions
                {summary && (
                  <Badge className="ml-2">{summary.total_transactions}</Badge>
                )}
              </h3>
              {summary && (
                <p className="text-sm text-gray-500">
                  Total: <span className="font-bold text-green-600">{formatCurrency(summary.total_sales)}</span>
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No transactions found for this date</p>
            </div>
          ) : (
            <div className="divide-y">
              {transactions.map((transaction) => (
                <div 
                  key={transaction.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <div 
                    className="p-4 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedTransaction(
                      expandedTransaction === transaction.id ? null : transaction.id
                    )}
                    data-testid={`transaction-${transaction.order_number}`}
                  >
                    <PaymentIcon method={transaction.payment_method} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-800">
                          #{transaction.order_number}
                        </span>
                        {transaction.transfer_code && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                            Transfer
                          </Badge>
                        )}
                        {transaction.order_type === 'delivery' && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-600 border-orange-200">
                            Delivery
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(transaction.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {transaction.cashier_name}
                        </span>
                        {transaction.customer_name && transaction.customer_name !== 'Walk-in Customer' && (
                          <span className="text-blue-600">
                            → {transaction.customer_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-800">
                        {formatCurrency(transaction.total)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {transaction.items_count} item{transaction.items_count !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <div className="text-gray-400">
                      {expandedTransaction === transaction.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedTransaction === transaction.id && (
                    <div className="px-4 pb-4 bg-gray-50 border-t">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3">
                        <div>
                          <p className="text-xs text-gray-500">Subtotal</p>
                          <p className="font-medium">{formatCurrency(transaction.subtotal)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Tax</p>
                          <p className="font-medium">{formatCurrency(transaction.tax)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Discount</p>
                          <p className="font-medium text-red-600">-{formatCurrency(transaction.discount || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Payment</p>
                          <p className="font-medium capitalize">{transaction.payment_method}</p>
                        </div>
                      </div>
                      
                      {transaction.payment_method === 'cash' && (
                        <div className="flex items-center gap-6 pt-2 border-t">
                          <div>
                            <p className="text-xs text-gray-500">Cash Received</p>
                            <p className="font-medium text-green-600">{formatCurrency(transaction.cash_received)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Change Due</p>
                            <p className="font-medium text-amber-600">{formatCurrency(transaction.change_due)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="pos-sales-report"], [data-testid="pos-sales-report"] * {
            visibility: visible;
          }
          [data-testid="pos-sales-report"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default POSSalesReport;
